import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import {
  Card,
  Table,
  Alert,
  Spinner,
  Button,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import { FaEye, FaCheck } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SuccessModal from "../components/modals/SuccessModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { useNotificationModal } from "../hooks/useNotificationModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function StockAllocationHistory() {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);

  useEffect(() => {}, [userData]);

  const [gtns, setGtns] = useState([]);
  const [filteredGtns, setFilteredGtns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setSuccess] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [selectedGTN, setSelectedGTN] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'reject'
  const [rejectReason, setRejectReason] = useState("");

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  // Column search states
  const [columnSearch, setColumnSearch] = useState({
    gtn_number: "",
    from_site: "",
    to_site: "",
    status: "",
    created_at: "",
  });

  const fetchGTNs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (userData?.company_code)
        queryParams.append("company_code", userData.company_code);

      const response = await fetch(
        `${BASE_URL}/api/admin/stock-allocation/get-all-gtns?${queryParams.toString()}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch GTNs");
      }

      const data = await response.json();

      if (data.success) {
        setGtns(data.gtns);
        setFilteredGtns(data.gtns);
      } else {
        setError(data.message || "Failed to fetch GTNs");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching GTNs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply column-wise search
  useEffect(() => {
    let filtered = [...gtns];

    // Filter by GTN Number
    if (columnSearch.gtn_number) {
      filtered = filtered.filter((gtn) =>
        gtn.gtn_number
          .toString()
          .toLowerCase()
          .includes(columnSearch.gtn_number.toLowerCase())
      );
    }

    // Filter by From Site
    if (columnSearch.from_site) {
      filtered = filtered.filter(
        (gtn) =>
          gtn.from_site_name
            ?.toLowerCase()
            .includes(columnSearch.from_site.toLowerCase()) ||
          gtn.from_site_code
            ?.toLowerCase()
            .includes(columnSearch.from_site.toLowerCase())
      );
    }

    // Filter by To Site
    if (columnSearch.to_site) {
      filtered = filtered.filter(
        (gtn) =>
          gtn.to_site_name
            ?.toLowerCase()
            .includes(columnSearch.to_site.toLowerCase()) ||
          gtn.to_site_code
            ?.toLowerCase()
            .includes(columnSearch.to_site.toLowerCase())
      );
    }

    // Filter by Status
    if (columnSearch.status) {
      filtered = filtered.filter((gtn) =>
        gtn.status?.toLowerCase().includes(columnSearch.status.toLowerCase())
      );
    }

    // Filter by Created At (date search)
    if (columnSearch.created_at) {
      filtered = filtered.filter((gtn) =>
        new Date(gtn.created_at)
          .toLocaleDateString()
          .toLowerCase()
          .includes(columnSearch.created_at.toLowerCase())
      );
    }

    setFilteredGtns(filtered);
  }, [columnSearch, gtns]);

  useEffect(() => {
    fetchGTNs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleColumnSearchChange = (key, value) => {
    setColumnSearch((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearColumnSearch = () => {
    setColumnSearch({
      gtn_number: "",
      from_site: "",
      to_site: "",
      status: "",
      created_at: "",
    });
  };

const getStatusColor = (status) => {
  const statusColors = {
    pending: "orange",
    approved: "green",
    partially_received: "deepskyblue",
    received: "blue",
    rejected: "red",
  };

  return statusColors[status.toLowerCase()] || "black";
};

  const handleApproveGTN = async () => {
    if (!selectedGTN) return;

    setApprovalLoading(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/api/admin/stock-allocation/update-status/${selectedGTN.gtn_id}`,
        {
          company_code: userData.company_code,
          status: "approved",
          user_id: userData.id,
          gtn_number: selectedGTN.gtn_number,
        }
      );

      if (response.data.success) {
        showSuccess(
          `GTN #${selectedGTN.gtn_id} approved successfully!`,
          "Success"
        );
        setShowConfirmModal(false);
        setSelectedGTN(null);
        setConfirmAction(null);
        setTimeout(() => setSuccess(null), 5000);
        fetchGTNs();
      } else {
        throw new Error(response.data.message || "Failed to approve GTN");
      }
    } catch (err) {
      console.error("Error approving GTN:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Error approving GTN. Please try again.",
        "Error"
      );
      setTimeout(() => setError(null), 5000);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleRejectGTN = async () => {
    if (!selectedGTN) return;

    setApprovalLoading(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/api/admin/stock-allocation/update-status/${selectedGTN.gtn_id}`,
        {
          company_code: userData.company_code,
          status: "rejected",
          user_id: userData.id,
          gtn_number: selectedGTN.gtn_number,
          rejection_reason: rejectReason,
        }
      );

      if (response.data.success) {
        showSuccess(
          `GTN #${selectedGTN.gtn_id} has been rejected successfully.`,
          "Success"
        );
        setShowConfirmModal(false);
        setSelectedGTN(null);
        setConfirmAction(null);
        setRejectReason("");
        fetchGTNs();
      } else {
        setError(response.data.message || "Failed to reject GTN");
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      console.error("Error rejecting GTN:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Error rejecting GTN. Please try again.",
        "Error"
      );
      setTimeout(() => setError(null), 5000);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleConfirmAction = () => {
    if (confirmAction === "approve") {
      handleApproveGTN();
    } else if (confirmAction === "reject") {
      handleRejectGTN();
    }
  };

  return (
    <div className="admin-container mt-4">
      <div className="style-header">
        <h1>Stock Allocation History</h1>
        <Button
          variant="primary"
          onClick={() => navigate("/warehouse/allocate-stock")}
        >
          Create GTN
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Summary Card */}
      <div className="row mb-4">
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted">Total</h5>
              <h2 className="mb-0 text-info">{gtns.length}</h2>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted">Pending</h5>
              <h2 className="mb-0 text-warning">
                {gtns.filter((g) => g.status === "pending").length}
              </h2>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted">Approved</h5>
              <h2 className="mb-0 text-info">
                {gtns.filter((g) => g.status === "approved").length}
              </h2>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted">Received</h5>
              <h2 className="mb-0 text-success">
                {gtns.filter((g) => g.status === "received").length}
              </h2>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted"> Partially Received</h5>
              <h2 className="mb-0 text-success">
                {gtns.filter((g) => g.status === "partially_received").length}
              </h2>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted">Rejected</h5>
              <h2 className="mb-0 text-danger">
                {gtns.filter((g) => g.status === "rejected").length}
              </h2>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* GTN List Card */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="loc-titles">GTN List ({gtns.length})</h3>
            <div className="d-flex gap-2">
              {/* <Button variant="primary" size="sm">
                <FaDownload className="me-1" />
                Export
              </Button> */}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : gtns.length === 0 ? (
            <Alert variant="info">No GTNs found</Alert>
          ) : (
            <>
              {/* Clear Search Button */}
              {Object.values(columnSearch).some((val) => val !== "") && (
                <div className="mb-3 text-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearColumnSearch}
                  >
                    Clear Column Search
                  </Button>
                </div>
              )}

              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>GTN Number</th>
                    {/* <th>From Site</th> */}
                    <th>To Site</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                  {/* Column Search Row */}
                  <tr className="table-search-row">
                    <th></th>
                    <th>
                      <InputGroup size="sm">
                        <FormControl
                          placeholder="Search GTN Number..."
                          value={columnSearch.gtn_number}
                          onChange={(e) =>
                            handleColumnSearchChange(
                              "gtn_number",
                              e.target.value,
                            )
                          }
                        />
                      </InputGroup>
                    </th>
                    <th>
                      <InputGroup size="sm">
                        <FormControl
                          placeholder="Search to site..."
                          value={columnSearch.to_site}
                          onChange={(e) =>
                            handleColumnSearchChange("to_site", e.target.value)
                          }
                        />
                      </InputGroup>
                    </th>
                    <th>
                      <InputGroup size="sm">
                        <FormControl
                          placeholder="Search status..."
                          value={columnSearch.status}
                          onChange={(e) =>
                            handleColumnSearchChange("status", e.target.value)
                          }
                        />
                      </InputGroup>
                    </th>
                    <th>
                      <InputGroup size="sm">
                        <FormControl
                          placeholder="Search date..."
                          value={columnSearch.created_at}
                          onChange={(e) =>
                            handleColumnSearchChange(
                              "created_at",
                              e.target.value,
                            )
                          }
                        />
                      </InputGroup>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGtns.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <Alert variant="warning" className="mb-0">
                          No results found matching your search criteria
                        </Alert>
                      </td>
                    </tr>
                  ) : (
                    filteredGtns.map((gtn) => (
                      <tr key={gtn.gtn_id}>
                        <td style={{ height: "5px" }}>
                          {filteredGtns.indexOf(gtn) + 1}
                        </td>
                        <td style={{ height: "5px" }}>{gtn.gtn_number}</td>
                        <td style={{ height: "5px" }}>
                          <div>
                            {gtn.to_site_name}{" "}
                            <small className="text-muted ms-2">
                              ({gtn.to_site_code})
                            </small>
                          </div>
                        </td>
                        <td
                          style={{ color: getStatusColor(gtn.status), textTransform: "uppercase", height: "5px", fontWeight: "600" }}
                        >
                          {gtn.status}
                        </td>
                        <td style={{ height: "5px" }}>
                          <div>
                            {new Date(gtn.created_at).toLocaleDateString()}{" "}
                            <small className="text-muted ms-2">
                              {new Date(gtn.created_at).toLocaleTimeString()}
                            </small>
                          </div>
                        </td>
                        <td style={{ height: "5px" }}>
                          <div className="d-flex gap-2 align-items-center">
                            <FaEye
                              className="action-icon"
                              title="View Details"
                              style={{
                                cursor: "pointer",
                                color: "#007bff",
                                fontSize: "17px",
                              }}
                              onClick={() =>
                                navigate(
                                  `/warehouse/allocate-stock/details/${gtn.gtn_id}`,
                                )
                              }
                            />
                            {gtn.status === "pending" &&
                              userData?.role === "Company_Admin" && (
                                <>
                                  <FaCheck
                                    className="action-icon"
                                    title="Approve GTN"
                                    style={{
                                      cursor: "pointer",
                                      color: "#28a745",
                                      fontSize: "17px",
                                    }}
                                    onClick={() => {
                                      setSelectedGTN(gtn);
                                      setConfirmAction("approve");
                                      setShowConfirmModal(true);
                                    }}
                                  />
                                  <MdOutlineCancel
                                    className="action-icon"
                                    title="Reject GTN"
                                    style={{
                                      cursor: "pointer",
                                      color: "#dc3545",
                                      fontSize: "17px",
                                    }}
                                    onClick={() => {
                                      setSelectedGTN(gtn);
                                      setConfirmAction("reject");
                                      setShowConfirmModal(true);
                                    }}
                                  />
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Confirm Modal for Approve/Reject */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setSelectedGTN(null);
          setConfirmAction(null);
          setRejectReason("");
        }}
        onConfirm={handleConfirmAction}
        title={
          confirmAction === "approve"
            ? "Approve GTN"
            : confirmAction === "reject"
              ? "Reject GTN"
              : "Confirm Action"
        }
        message={
          confirmAction === "approve"
            ? `Are you sure you want to approve GTN #${selectedGTN?.gtn_id}? Check Before Approve!`
            : confirmAction === "reject"
              ? `Are you sure you want to reject GTN #${selectedGTN?.gtn_id}? Check Before Reject!`
              : "Are you sure?"
        }
        confirmText={confirmAction === "approve" ? "Approve" : "Reject"}
        confirmVariant={confirmAction === "approve" ? "success" : "danger"}
        loading={approvalLoading}
      >
        {confirmAction === "reject" && (
          <div className="mb-3">
            <label htmlFor="rejectReason" className="form-label">
              <strong>Rejection Reason (Optional)</strong>
            </label>
            <FormControl
              id="rejectReason"
              as="textarea"
              rows={3}
              placeholder="Enter the reason for rejecting this GTN..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={approvalLoading}
            />
          </div>
        )}
      </ConfirmModal>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="danger"
      />
    </div>
  );
}
